import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
  message
} from "antd";
import { CloudDownloadOutlined, NotificationOutlined, UnlockOutlined } from "@ant-design/icons";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import PageHeader from "../shared/PageHeader";
import Amount from "../shared/Amount";
import { formatDate } from "../shared/money";
import * as api from "./api";

const REASONS = {
  scheduled: { color: "blue", label: "Already in a collection run" },
  not_prenotified: { color: "orange", label: "Not pre-notified" },
  not_due: { color: "default", label: "Not due yet" },
  mandate_missing: { color: "red", label: "No SEPA mandate" },
  invalid_iban: { color: "red", label: "Invalid IBAN" },
  retention_only: { color: "default", label: "Only the retention is open" }
};
const BATCH_STATUS = {
  draft: { color: "default", label: "Draft" },
  generated: { color: "blue", label: "Generated" },
  uploaded: { color: "gold", label: "Uploaded" },
  executed: { color: "green", label: "Executed" },
  cancelled: { color: "red", label: "Cancelled" }
};

const fromDay = v => (v ? v.format("YYYY-MM-DD") : "");

// ---------------------------------------------------------------------------------------------
// SEPA direct debit collection (PAY-5)
// ---------------------------------------------------------------------------------------------

const DirectDebit = () => {
  const { t } = useTranslation("accounting");
  const [date, setDate] = useState(dayjs());
  const [candidates, setCandidates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.debitCandidates({ date: fromDay(date) }),
        api.listDebitRuns()
      ]);
      setCandidates(Array.isArray(c.data?.invoices) ? c.data.invoices : []);
      setRuns(Array.isArray(r.data?.batches) ? r.data.batches : []);
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setLoading(false);
    }
  }, [date]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api
      .listBankAccounts()
      .then(res => {
        const list = (Array.isArray(res.data?.accounts) ? res.data.accounts : []).filter(
          a => a.active !== false && a.kind !== "paypal" && a.kind !== "credit_card"
        );
        setAccounts(list);
        if (list[0]) setAccountId(list[0]._id);
      })
      .catch(() => setAccounts([]));
  }, []);

  const ready = candidates.filter(c => c.ready);
  const readyTotal = ready.reduce((s, c) => s + c.amount, 0);

  const notify = async row => {
    try {
      const res = await api.prenotify(row._id);
      message.success(
        `${t("Pre-notified; collection on")} ${formatDate(res.data?.invoice?.directDebit?.collectionDate)}`
      );
      load();
    } catch (err) {
      message.error(api.errorText(err));
    }
  };

  const createRun = async () => {
    setCreating(true);
    try {
      const res = await api.createDebitRun({ date: fromDay(date), bankAccountId: accountId });
      const batches = res.data?.batches || [];
      if (!batches.length) message.info(t("Nothing to collect on this date"));
      else message.success(`${t("Collection run created")}: ${batches.length}`);
      if (res.data?.skipped?.length)
        message.warning(
          `${t("Skipped")}: ${res.data.skipped.map(s => s.invoiceNumber).join(", ")}`
        );
      load();
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setCreating(false);
    }
  };

  const download = async batch => {
    try {
      await api.openBlob(
        api.debitRunXml(batch._id),
        `pain008-${batch.executionDate}-${batch.messageId}.xml`
      );
    } catch (err) {
      message.error(api.errorText(err));
    }
  };

  const columns = [
    {
      title: t("Invoice"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      render: (v, r) => (
        <Space direction="vertical" size={0} className="acct-wrap">
          <Typography.Text strong>{v}</Typography.Text>
          <Typography.Text type="secondary">{r.customer}</Typography.Text>
        </Space>
      )
    },
    {
      title: t("Due date"),
      dataIndex: "dueDate",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Collection date"),
      dataIndex: "collectionDate",
      render: v => <span className="acct-num">{v ? formatDate(v) : "—"}</span>
    },
    {
      title: t("Mandate"),
      key: "mandate",
      render: (_, r) =>
        r.mandate?.reference ? (
          <span>
            {r.mandate.reference} <Tag>{r.mandate.sequence}</Tag>
          </span>
        ) : (
          "—"
        )
    },
    {
      title: t("Amount"),
      dataIndex: "amount",
      align: "end",
      mobile: "subtitle",
      render: v => <Amount cents={v} strong />
    },
    {
      title: t("Status"),
      key: "reason",
      render: (_, r) =>
        r.ready ? (
          <Tag color="green">{t("Ready")}</Tag>
        ) : (
          <Tag color={REASONS[r.reason]?.color}>{t(REASONS[r.reason]?.label || r.reason)}</Tag>
        )
    },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, r) =>
        ["not_prenotified", "not_due"].includes(r.reason) ? (
          <Button size="small" icon={<NotificationOutlined />} onClick={() => notify(r)}>
            {r.prenotifiedAt ? t("Pre-notify again") : t("Pre-notify")}
          </Button>
        ) : null
    }
  ];

  const runColumns = [
    {
      title: t("Collection date"),
      dataIndex: "executionDate",
      mobile: "title",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Reference"),
      dataIndex: "messageId",
      render: v => <span className="acct-wrap">{v}</span>
    },
    { title: t("Items"), key: "items", render: (_, r) => (r.items || []).length },
    {
      title: t("Amount"),
      dataIndex: "totalAmount",
      align: "end",
      render: v => <Amount cents={v} />
    },
    {
      title: t("Status"),
      dataIndex: "status",
      mobile: "subtitle",
      render: v => <Tag color={BATCH_STATUS[v]?.color}>{t(BATCH_STATUS[v]?.label || v)}</Tag>
    },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, r) => (
        <Button size="small" icon={<CloudDownloadOutlined />} onClick={() => download(r)}>
          {t("Download XML")}
        </Button>
      )
    }
  ];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "Invoices marked for direct debit are collected after the pre-notification period. The payment is booked when the collection appears on the bank account."
        )}
      />
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <span>{t("Collection date")}</span>
          <DatePicker
            value={date}
            onChange={v => setDate(v || dayjs())}
            format="DD.MM.YYYY"
            allowClear={false}
            disabledDate={d => d && d.isBefore(dayjs().startOf("day"))}
          />
          <Select
            style={{ minWidth: 220 }}
            value={accountId}
            onChange={setAccountId}
            placeholder={t("Bank account")}
            options={accounts.map(a => ({ value: a._id, label: `${a.name} · ${a.iban}` }))}
          />
          <Button
            type="primary"
            loading={creating}
            disabled={!ready.length || !accountId}
            onClick={createRun}
          >
            {t("Create collection run")} ({ready.length} · <Amount cents={readyTotal} />)
          </Button>
        </Space>
      </Card>
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={candidates}
        columns={columns}
        pagination={false}
      />
      <Typography.Title level={5} style={{ marginTop: 24 }}>
        {t("Collection runs")}
      </Typography.Title>
      <ResponsiveTable
        rowKey="_id"
        size="small"
        dataSource={runs}
        columns={runColumns}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Sicherheitseinbehalte (ABS-3)
// ---------------------------------------------------------------------------------------------

const Retentions = () => {
  const { t } = useTranslation("accounting");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listRetentions();
      setRows(Array.isArray(res.data?.retentions) ? res.data.retentions : []);
    } catch (err) {
      message.error(api.errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const release = async row => {
    try {
      await api.releaseRetention(row._id);
      message.success(t("Retention released"));
      load();
    } catch (err) {
      message.error(api.errorText(err));
    }
  };
  const today = dayjs().format("YYYY-MM-DD");
  const columns = [
    {
      title: t("Invoice"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      render: (v, r) => (
        <Space direction="vertical" size={0} className="acct-wrap">
          <Typography.Text strong>{v}</Typography.Text>
          <Typography.Text type="secondary">{r.customer}</Typography.Text>
        </Space>
      )
    },
    {
      title: t("Retention"),
      key: "amount",
      align: "end",
      mobile: "subtitle",
      render: (_, r) => <Amount cents={r.retention?.amount} strong />
    },
    {
      title: t("Due date"),
      key: "due",
      render: (_, r) => (
        <Space size={4}>
          <span className="acct-num">{formatDate(r.retention?.dueDate)}</span>
          {r.retention?.dueDate && r.retention.dueDate <= today ? (
            <Tag color="red">{t("Due")}</Tag>
          ) : null}
          {r.retention?.guarantee ? <Tag color="purple">{t("Guarantee")}</Tag> : null}
          {r.retention?.reminderSentAt ? <Tag>{t("Reminder created")}</Tag> : null}
        </Space>
      )
    },
    { title: t("Open"), dataIndex: "open", align: "end", render: v => <Amount cents={v} /> },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: (_, r) => (
        <Popconfirm
          title={t("Release the retention? It will no longer be shown as withheld.")}
          onConfirm={() => release(r)}
        >
          <Button size="small" icon={<UnlockOutlined />}>
            {t("Release")}
          </Button>
        </Popconfirm>
      )
    }
  ];
  return (
    <ResponsiveTable
      rowKey="_id"
      size="small"
      loading={loading}
      dataSource={rows}
      columns={columns}
      pagination={false}
    />
  );
};

// ---------------------------------------------------------------------------------------------

// Recurring invoice templates moved to the Invoices module (/user/invoices?view=recurring);
// this page keeps the SEPA collection runs and the retentions.
const Recurring = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [view, setView] = useState("debit");
  const options = useMemo(
    () => [
      { value: "debit", label: t("Direct debit") },
      { value: "retentions", label: t("Retentions") }
    ],
    [t]
  );
  return (
    <div>
      <PageHeader
        title={t("Direct debit and retentions")}
        subtitle={t("SEPA collection runs and retentions")}
      />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t("Recurring invoices are now written in the Invoices module.")}
        action={
          <Link to="/user/invoices?view=recurring">
            <Button size="small">{t("Open recurring invoices")}</Button>
          </Link>
        }
      />
      <Segmented
        block={isMobile}
        style={{ marginBottom: 16 }}
        value={view}
        onChange={setView}
        options={options}
      />
      {view === "debit" ? <DirectDebit /> : null}
      {view === "retentions" ? <Retentions /> : null}
    </div>
  );
};

export default Recurring;
